import { AxiosRequestConfig, AxiosResponse } from "axios";
import { isNil } from "lodash";
import { appendParams, ProductContractTypeFilter } from "../api/common";
import parkingProductsApi, { GetVehicleAssignmentsMyFleetQuery, GetVehicleAssignmentsQuery } from "../api/parkingProducts.api";
import businessPortalClient from "../businessPortalClient";
import { DataTableContextFilters, DateFilter } from "../components/DataTable/DataTableContext/DataTableContextProvider";
import { FmpFilters } from "../constants/filtering.constants";
import { GetBulkImportProgressResponseModel } from "../models/bulkImport/GetBulkImportProgressResponseModel";
import { GetBulkImportStatusResponseModel } from "../models/bulkImport/GetBulkImportStatusResponseModel";
import { StartBulkInvitationsRequestModel } from "../models/bulkImport/StartBulkInvitationsRequestModel";
import { ValidateDelegeesRequestModel } from "../models/bulkImport/ValidateDelegeesRequestModel";
import { ValidateDelegeesResponseModel } from "../models/bulkImport/ValidateDelegeesResponseModel";
import {
  AssignedParkingRightsWithPagination,
  DelegatedParkingRightsWithPagination,
  DelegeesWithPagination,
  DelegeeWithProductsModel,
  FlexBudgetUnitType,
  InviteDelegeeResponse,
  UpdateDelegeeResponse,
  VehicleAssignmentModel,
  VehicleAssignmentsMyFleetWithPagination
} from "../models/delegations/DelegatedParkingProductsModel";
import { DeleteDelegationsRequestModel } from "../models/delegations/DeleteDelegationsRequestModel";
import { WithFiltersAndPaging } from "../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../models/ForFleetManager";
import { LocatedParkingProduct } from "../models/LocatedParkingProduct";
import { ParkingProductModel, ResendDelegeeInviteRequest } from "../models/ParkingProductModel";
import { ParkingRightsRequestModel } from "../models/ParkingRightsRequestModel";
import { RevokeParkingRightsModel } from "../models/RevokeParkingRightsModel";
import { AssignVehicleRequestModel } from "../models/vehicle-assignments/AssignVehicleRequestModel";
import { AssignVehicleResponseModel } from "../models/vehicle-assignments/AssignVehicleResponseModel";
import { CalculatedWhitelistDeltaResponse, CalculateWhitelistDeltaRequest } from "../models/vehicle-assignments/CalculateWhitelistDelta";
import { DeleteVehicleAssignmentsModel } from "../models/vehicle-assignments/DeleteVehicleAssignmentsModel";
import { GetBulkVehicleAssignmentsStatusResponseModel } from "../models/vehicle-assignments/GetBulkVehicleAssignmentsStatusResponseModel";
import { GetBulkVehicleImportProgressResponseModel } from "../models/vehicle-assignments/GetBulkVehicleImportProgressResponseModel";
import { RevokeVehicleAssignmentsModel } from "../models/vehicle-assignments/RevokeVehicleAssignmentsModel";
import { StartBulkVehicleAssignmentRequest } from "../models/vehicle-assignments/StartBulkVehicleAssignmentRequest";
import { UpdateVehicleRequestModel } from "../models/vehicle-assignments/UpdateVehicleRequestModel";
import { UpdateVehicleResponseModel } from "../models/vehicle-assignments/UpdateVehicleResponseModel";
import { ValidatedVehicleBulkUploadResponse, ValidateVehicleBulkUploadRequest } from "../models/vehicle-assignments/ValidateVehicleBulkUpload";
import { convertDateToIsoString, getLocalizedDate } from "../utils/date.utils";
import queryParam from "../utils/queryParam.utils";

const getParkingProducts = (
  seasonTicketOwnerCrmId: string,
  location: string
): Promise<AxiosResponse<ParkingProductModel[]>> => {
  return businessPortalClient.get(
    parkingProductsApi.getParkingProductsUrl(seasonTicketOwnerCrmId, location)
  );
};

const getLocatedParkingProducts = (
  seasonTicketOwnerCrmId: string,
  language: string
) => {
  return businessPortalClient.get<LocatedParkingProduct[]>(
    parkingProductsApi.getLocatedParkingProductsUrl(
      seasonTicketOwnerCrmId,
      language
    )
  );
};

const delegateParkingRights = (
  model: ParkingRightsRequestModel
): Promise<AxiosResponse<InviteDelegeeResponse>> => {
  return businessPortalClient.post(parkingProductsApi.getDelegateParkingRightsUrl(), model);
};

const updateDelegation = (
  model: ParkingRightsRequestModel
): Promise<AxiosResponse<UpdateDelegeeResponse>> => {
  return businessPortalClient.put(parkingProductsApi.getUpdateDelegationUrl(), model);
};

const getDelegatedParkingProducts = (
  delegatedParkingRightsRequest: WithFiltersAndPaging<ForFleetManager>
): Promise<AxiosResponse<DelegatedParkingRightsWithPagination>> => {
  const params = queryParam.buildQueryParam(
    delegatedParkingRightsRequest.filters as DataTableContextFilters[],
    delegatedParkingRightsRequest.pagination,
    (date: Date, _) => date.toISOString()
  );

  return businessPortalClient.get(
    parkingProductsApi.getDelegatedParkingRightsUrl(
      delegatedParkingRightsRequest.entity.seasonTicketOwnerCrmId,
      delegatedParkingRightsRequest.entity.language
    ),
    { params }
  );
};

export function convertFiltersToGetVehicleAssignmentsQuery(
  r: WithFiltersAndPaging<ForFleetManager>
): GetVehicleAssignmentsQuery {
  const { filters } = r;
  const convertDateFilter = (key: string) => {
    const dateFilter = filters?.find((x) => x.key === key)?.value as DateFilter | undefined;

    return isNil(dateFilter) || isNil(dateFilter.startDate) || isNil(dateFilter.endDate)
      ? undefined
      : convertDateToIsoString(dateFilter.startDate, dateFilter.endDate);
  };

  const convertDateFilterLocalized = (key: string) => {
    const dateFilter = filters?.find((x) => x.key === key)?.value as DateFilter | undefined;
    return {
        start: getLocalizedDate(dateFilter?.startDate) || undefined,
        end: getLocalizedDate(dateFilter?.endDate, true) || undefined
    };
  };

  const convertedAssignedDateFilter = convertDateFilter(FmpFilters.assignedDate);
  const convertedStartedDateFilter = convertDateFilter(FmpFilters.startedDate);
  const convertedActivationDateFilter = convertDateFilterLocalized(FmpFilters.activationDate);
  const convertedExpirationDateFilter = convertDateFilterLocalized(FmpFilters.expirationDate);
  const convertedActiveSessionDateFilter = convertDateFilter(FmpFilters.activeSessionDate);

  return {
    seasonTicketOwnerCrmId: r.entity.seasonTicketOwnerCrmId,
    placeId: Number(filters?.find((x) => x.key === FmpFilters.placeId)?.value),
    language: r.entity.language,
    page: r.pagination.pageNumber,
    pageSize: r.pagination.pageSize,
    assignedDateStart: convertedAssignedDateFilter?.start,
    assignedDateEnd: convertedAssignedDateFilter?.end,
    startedDateStart: convertedStartedDateFilter?.start,
    startedDateEnd: convertedStartedDateFilter?.end,
    activationDateStart: convertedActivationDateFilter?.start,
    activationDateEnd: convertedActivationDateFilter?.end,
    expirationDateStart: convertedExpirationDateFilter?.start,
    expirationDateEnd: convertedExpirationDateFilter?.end,
    activeSessionSinceStart: convertedActiveSessionDateFilter?.start,
    activeSessionSinceEnd: convertedActiveSessionDateFilter?.end,
    ascending: r.filters?.find((x) => x.key === FmpFilters.orderDesc)?.value === "false",
    numberPlateValue: filters?.find((x) => x.key === FmpFilters.numberPlate)?.value as any,
    pmcIds: filters?.find((x) => x.key === FmpFilters.products)?.value as any,
    sortBy: filters?.find((x) => x.key === FmpFilters.sortBy)?.value as any,
    description: filters?.find((x) => x.key === FmpFilters.description)?.value as any
  };
}

export function convertFiltersToGetVehicleAssignmentsMyFleetQuery(
  r: WithFiltersAndPaging<ForFleetManager>
): GetVehicleAssignmentsMyFleetQuery {
  const { placeId: _placeId, ...query } = convertFiltersToGetVehicleAssignmentsQuery(r);
  return query;
}

async function getVehicles(
  query: GetVehicleAssignmentsQuery,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<AssignedParkingRightsWithPagination>> {
  const url = parkingProductsApi.getVehicleAssignmentsUrl(query);

  type ApiDto = {
    items: Array<{
      vehicleAssignmentId: string;
      parkingRightId: string;
      pmcId: number;
      pmcName: string;
      locationName: string;
      description?: string;
      numberPlate: {
        countryCode: string;
        value: string;
      };
      assignedAt: string;
      startedAt: string;
      activationDate: string | null;
      expirationDate: string | null;
      flexBudgetRemainingTime: number | null;
      flexBudgetUnitType: FlexBudgetUnitType | null;
      activeSessionSince: string | null;
    }>;
    filteredCount: number;
  };

  const map = (dto: ApiDto): AssignedParkingRightsWithPagination => {
    const pages = Math.ceil(dto.filteredCount / query.pageSize);

    return {
      assignedParkingRights: dto.items.map((x) => ({
        vehicleAssignmentId: x.vehicleAssignmentId,
        parkingRight: {
          id: x.parkingRightId,
          pmc: x.pmcId,
          location: x.locationName,
          name: x.pmcName,
        },
        assigned: new Date(x.assignedAt),
        startedAt: x.startedAt ? new Date(x.startedAt) : null,
        activationDate: x.activationDate ?? null,
        expirationDate: x.expirationDate ?? null,
        vehicle: {
          countryCode: x.numberPlate.countryCode,
          identifier: x.numberPlate.value,
          description: x.description,
        },
        flexBudgetRemainingTime: x.flexBudgetRemainingTime === null ? undefined : x.flexBudgetRemainingTime.toString(),
        flexBudgetUnitType: x.flexBudgetUnitType === null ? undefined : x.flexBudgetUnitType,
        activeSessionSince: x.activeSessionSince ?? null,
      })),
      pages,
      totalRecords: dto.filteredCount,
    };
  };

  const response = await businessPortalClient.get<ApiDto>(url, config);

  return {
    ...response,
    data: response.data && map(response.data),
  };
}

const getDelegeeDetails = (
  registrationId: string,
  language: string,
  seasonTicketOwnerCrmId: string
): Promise<AxiosResponse<DelegeeWithProductsModel>> => {
  return businessPortalClient.get(
    parkingProductsApi.getDelegeeDetailsUrl(registrationId, language, seasonTicketOwnerCrmId)
  );
};

const deleteDelegee = (registrationId: string, seasonTicketOwnerCrmId: string, sendRevocationConfirmationEmail: boolean): Promise<AxiosResponse> => {
  return businessPortalClient.delete(parkingProductsApi.getDeleteDelegeeUrl(registrationId, seasonTicketOwnerCrmId), {
    data: { sendRevocationConfirmationEmail }
  });
};

const resendDelegeeInvite = (
  registrationId: string,
  seasonTicketOwnerCrmId: string,
  request: ResendDelegeeInviteRequest
): Promise<AxiosResponse> => {
  return businessPortalClient.post(
    parkingProductsApi.getResendDelegeeInviteUrl(
      registrationId,
      seasonTicketOwnerCrmId
    ),
    request
  );
};

const getDelegees = (
  request: WithFiltersAndPaging<ForFleetManager>,
  productContractTypeFilter: ProductContractTypeFilter
): Promise<AxiosResponse<DelegeesWithPagination>> => {
  const params = queryParam.buildQueryParam(
    request.filters as DataTableContextFilters[],
    request.pagination,
    (date: Date, _?: any) => date.toISOString()
  );

  appendParams(params, productContractTypeFilter);

  return businessPortalClient.get(
    parkingProductsApi.getDelegeesUrl(
      request.entity.seasonTicketOwnerCrmId,
      request.entity.language
    ),
    { params }
  );
};

const validateDelegees = (
  seasonTicketOwnerCrmId: string,
  req: ValidateDelegeesRequestModel
): Promise<AxiosResponse<ValidateDelegeesResponseModel>> => {
  return businessPortalClient.post(
    parkingProductsApi.getValidateDelegeesUrl(seasonTicketOwnerCrmId),
    req
  );
};

const getDelegeesValidationResult = (
    seasonTicketOwnerCrmId: string,
    fileReference: string,
    language: string
): Promise<AxiosResponse<Blob>> => {
    return businessPortalClient.get(
        parkingProductsApi.getDelegeesValidationResultUrl(seasonTicketOwnerCrmId, fileReference,language),
        {
            responseType: "blob",
        }
    );
};

const getDelegeesInvitationResult = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  language: string
): Promise<AxiosResponse<Blob>> => {
  return businessPortalClient.get(
      parkingProductsApi.getDelegeesInvitationResultUrl(seasonTicketOwnerCrmId, fileReference, language),
      {
          responseType: "blob",
      }
  );
};

const startBulkInvitations  = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  req: StartBulkInvitationsRequestModel): Promise<AxiosResponse<ValidateDelegeesResponseModel>> => {
    return businessPortalClient.post(parkingProductsApi.getStartBulkInvitationsUrl(seasonTicketOwnerCrmId, fileReference), req);
  }


const startBulkVehicleAssignment  = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  startBulkVehicleAssignmentRequest: StartBulkVehicleAssignmentRequest): Promise<AxiosResponse> => {
    return businessPortalClient.post(parkingProductsApi.getStartBulkVehicleAssignmentUrl(seasonTicketOwnerCrmId, fileReference), startBulkVehicleAssignmentRequest);
  }


const getBulkImportProgress = (
    seasonTicketOwnerCrmId: string,
    fileReference: string
): Promise<AxiosResponse<GetBulkImportProgressResponseModel>> => {
    return businessPortalClient.get(parkingProductsApi.getBulkImportProgressUrl(seasonTicketOwnerCrmId, fileReference));
};

const getBulkImportStatus = (
  seasonTicketOwnerCrmId: string,
): Promise<AxiosResponse<GetBulkImportStatusResponseModel>> => {
  return businessPortalClient.get(parkingProductsApi.getBulkImportStatusUrl(seasonTicketOwnerCrmId));
}

const assignVehicle = (
  seasonTicketOwnerCrmId: string,
  req: AssignVehicleRequestModel
): Promise<AxiosResponse<AssignVehicleResponseModel>> => {
  return businessPortalClient.post(parkingProductsApi.getAssignVehicleUrl(seasonTicketOwnerCrmId), req);
}

const updateVehicleAssignment = (
  seasonTicketOwnerCrmId: string,
  aggregateId: string,
  req: UpdateVehicleRequestModel
): Promise<AxiosResponse<UpdateVehicleResponseModel>> => {
  return businessPortalClient.post(parkingProductsApi.getUpdateVehicleAssignmentUrl(seasonTicketOwnerCrmId, aggregateId), req);
}

const getVehicleAssignment = (
  aggregateId: string
): Promise<AxiosResponse<VehicleAssignmentModel>> => {
  return businessPortalClient.get(parkingProductsApi.getVehicleAssignmentUrl(aggregateId));
}

async function getVehicleAssignmentsMyFleet(query: GetVehicleAssignmentsMyFleetQuery): Promise<AxiosResponse<VehicleAssignmentsMyFleetWithPagination>> {
  const url = parkingProductsApi.getVehicleAssignmentsMyFleetUrl(query);

  type vehicleAssignmentProductDto = {
    parkingRightId: string;
    pmcId: number;
    pmcName: string;
    locationName: string;
    activeSessionSince?: string | null;
  }
  type vehicleAssignmentsDto = {
    items: Array<{
      vehicleAssignmentId: string;
      description?: string;
      numberPlate: {
        countryCode: string;
        value: string;
      };
      assignedAt: string;
      products: vehicleAssignmentProductDto[]
    }>;
    filteredCount: number;
  };

  const map = (dto: vehicleAssignmentsDto): VehicleAssignmentsMyFleetWithPagination => {
    const pages = Math.ceil(dto.filteredCount / query.pageSize);

    return {
      vehicleAssignments: dto.items.map((x) => ({
        vehicleAssignmentId: x.vehicleAssignmentId,
        assignedAt: new Date(x.assignedAt),
        vehicle: {
          countryCode: x.numberPlate.countryCode,
          identifier: x.numberPlate.value,
          description: x.description,
        },
        products: x.products.map(p => { return { parkingRightId: p.parkingRightId, pmcId: p.pmcId, pmcName: p.pmcName, locationName: p.locationName, activeSessionSince: p.activeSessionSince ?? null }})
      })),
      pages,
      totalRecords: dto.filteredCount,
    };
  };

  const response = await businessPortalClient.get<vehicleAssignmentsDto>(url);

  return {
    ...response,
    data: response.data && map(response.data),
  };
}

async function getVehicleAssignmentsMyFleetOverview(query: GetVehicleAssignmentsMyFleetQuery) {
  return  parkingProductsApi.getVehicleAssignmentsMyFleetOverview(query);
}

const revokeParkingRights = (
  model: RevokeParkingRightsModel
): Promise<AxiosResponse> => {
  return businessPortalClient.post(parkingProductsApi.getRevokeParkingRightsUrl(), model);
};

const deleteDelegations = (request: DeleteDelegationsRequestModel) => {
  return businessPortalClient.post(parkingProductsApi.getDeleteDelegeesUrl(), request);
};

const revokeVehicleAssignments = (
  model: RevokeVehicleAssignmentsModel
): Promise<AxiosResponse> => {
  return businessPortalClient.post(parkingProductsApi.getRevokeVehicleAssignmentsUrl(), model);
};

const deleteVehicleAssignments = (
    model: DeleteVehicleAssignmentsModel
): Promise<AxiosResponse> => {
    return businessPortalClient.post(parkingProductsApi.getDeleteVehicleAssignmentsUrl(), model);
};

const calculateWhitelistDelta = (
    req: CalculateWhitelistDeltaRequest
): Promise<AxiosResponse<CalculatedWhitelistDeltaResponse>> => {
    const jsonFile = new File([JSON.stringify(req)], "data.json", {
        type: "application/json",
    });
    const formData = new FormData();
    formData.append("file", jsonFile);

    return businessPortalClient.post(
        parkingProductsApi.getCalculateWhitelistDeltaUrl(),
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
                "filename": "whitelistdata", // Add the filename header
            },
        });
};

const getBulkVehicleImportProgress = (
  seasonTicketOwnerCrmId: string,
  fileReference: string
): Promise<AxiosResponse<GetBulkVehicleImportProgressResponseModel>> => {
  return businessPortalClient.get(parkingProductsApi.getBulkVehicleImportProgressUrl(seasonTicketOwnerCrmId, fileReference));
};

const getBulkVehicleAsignmentsStatus = (
    seasonTicketOwnerCrmId: string,
): Promise<AxiosResponse<GetBulkVehicleAssignmentsStatusResponseModel>> => {
    return businessPortalClient.get(parkingProductsApi.getBulkVehicleAssignmentsStatusUrl(seasonTicketOwnerCrmId));
};

const validateVehicleBulkUpload = (
  req: ValidateVehicleBulkUploadRequest
): Promise<AxiosResponse<ValidatedVehicleBulkUploadResponse>> => {
  return businessPortalClient.post(parkingProductsApi.getValidateVehicleBulkUploadUrl(), req);
};

const getBulkVehicleAssignmentsErrorsFile = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  language: string,
): Promise<AxiosResponse<Blob>> => {
  return businessPortalClient.get(
    parkingProductsApi.getBulkVehicleAssignmentsErrorResultUrl(seasonTicketOwnerCrmId, fileReference, language),
    {
      responseType: "blob",
    });
};

export default {
  getParkingProducts,
  getLocatedParkingProducts,
  delegateParkingRights,
  updateDelegation,
  getDelegatedParkingProducts,
  getDelegeeDetails,
  getVehicles,
  deleteDelegee,
  resendDelegeeInvite,
  getDelegees,
  revokeParkingRights,
  deleteDelegations,
  validateDelegees,
  getDelegeesValidationResult,
  getDelegeesInvitationResult,
  startBulkInvitations,
  getBulkImportProgress,
  getBulkImportStatus,
  assignVehicle,
  updateVehicleAssignment,
  getVehicleAssignment,
  getVehicleAssignmentsMyFleet,
  getVehicleAssignmentsMyFleetOverview,
  revokeVehicleAssignments,
  deleteVehicleAssignments,
  calculateWhitelistDelta,
  startBulkVehicleAssignment,
  getBulkVehicleImportProgress,
  getBulkVehicleAsignmentsStatus,
  validateVehicleBulkUpload,
  getBulkVehicleAssignmentsErrorsFile
};